"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { submitData } from '@/utils/api/getData';
import { apiRoutes } from '@/utils/api/routes';

export const connectFormSchema = z.object({
    fullName: z
        .string()
        .min(1, "Full name is required")
        .min(2, "Full name must be at least 2 characters"),
    contactNumber: z
        .string()
        .min(1, "Contact number is required")
        .regex(/^\+?[0-9\s()-]{7,20}$/, "Please enter a valid contact number"),
    email: z
        .string()
        .min(1, "Email is required")
        .email("Please enter a valid email address"),
    companyName: z
        .string()
        .optional(),
    requirement: z
        .string()
        .min(1, "Requirement is required")
        .min(5, "Requirement must be at least 5 characters"),
});

export type ConnectFormValues = z.infer<typeof connectFormSchema>;

export interface ConnectFormProps {
    professionalId?: string;
    professionalName?: string;
    user?: { user: string; token: string; type: string; email?: string } | null;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export function ConnectForm({
    professionalId,
    professionalName,
    user,
    onSuccess,
    onCancel,
}: ConnectFormProps) {
    const [loading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const form = useForm<ConnectFormValues>({
        resolver: zodResolver(connectFormSchema),
        defaultValues: {
            fullName: "",
            contactNumber: "",
            email: user?.email || "",
            requirement: "",
            companyName: "",
        },
    });

    useEffect(() => {
        if (user?.email) {
            form.setValue("email", user.email, {
                shouldValidate: true,
            });
        }
    }, [user?.email, form]);

    const resetForm = () => {
        form.reset({
            fullName: "",
            contactNumber: "",
            email: user?.email || "",
            requirement: "",
            companyName: "",
        });
    };

    const onSubmit = async (values: ConnectFormValues) => {
        if (!user?.token) {
            toast.error("Please log in to send a connection request.");
            return;
        }

        try {
            setLoading(true);
            const response = await submitData({
                url: apiRoutes.clientRoutes.connectRequest,
                token: user.token,
                options: {
                    method: "POST",
                    body: JSON.stringify({
                        professional: professionalId,
                        type: user?.type,
                        note: values.requirement,
                        fullName: values.fullName,
                        contactNumber: values.contactNumber,
                        email: values.email,
                        requirement: values.requirement,
                        companyName: values.companyName || "",
                    }),
                },
            });

            if (response?.status || response?.success) {
                setIsSuccess(true);
                resetForm();
                toast.success(`Connection request sent to ${professionalName || 'professional'}!`);
                onSuccess?.();
            } else {
                toast.error(response?.message || "Failed to send connection request. Please try again.");
            }
        } catch {
            toast.error("Failed to send connection request. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsSuccess(false);
        resetForm();
        onCancel?.();
    };


    if (isSuccess) {
        return (
            <div className="flex flex-col items-center justify-center py-6 text-center animate-in fade-in zoom-in-95 duration-200">
                <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4 shadow-sm border border-emerald-100">
                    <CheckCircle2 className="w-8 h-8" />
                </div>
                <DialogTitle className="text-2xl font-bold text-slate-900 tracking-tight">
                    Request Sent Successfully!
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-600 mt-2 max-w-sm leading-relaxed">
                    Your connection request has been sent to <span className="font-semibold text-slate-800 capitalize">{professionalName || "the professional"}</span>. Please complete profile to chat with artha expert.
                </DialogDescription>
                <div className="mt-8 w-full flex justify-center">
                    <Link
                        href="/onboarding"
                        className="w-full sm:w-auto min-w-[200px] py-2.5 px-6 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-md hover:shadow-lg transition-all cursor-pointer text-center inline-flex items-center justify-center"
                    >
                        Complete Profile
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <DialogHeader>
                <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                    Connect with <span className='capitalize'>{professionalName || "Expert"}</span>
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-600 mt-1 leading-relaxed">
                    Fill in your details and requirement to send a connection request.
                </DialogDescription>
            </DialogHeader>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Full Name */}
                        <FormField
                            control={form.control}
                            name="fullName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                                        Full Name <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="e.g. John Doe"
                                            className="rounded-xl border-slate-200 h-10 text-sm"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Contact Number */}
                        <FormField
                            control={form.control}
                            name="contactNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                                        Contact Number <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="e.g. +91 98765 43210"
                                            className="rounded-xl border-slate-200 h-10 text-sm"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Email */}
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                                        Email <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="email"
                                            placeholder="e.g. john@example.com"
                                            className="rounded-xl border-slate-200 h-10 text-sm"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Company Name (Optional) */}
                        <FormField
                            control={form.control}
                            name="companyName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                                        Company Name <span className="text-slate-400 font-normal lowercase">(optional)</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="e.g. Acme Corp"
                                            className="rounded-xl border-slate-200 h-10 text-sm"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Requirement */}
                    <FormField
                        control={form.control}
                        name="requirement"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                                    Requirement <span className="text-red-500">*</span>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        rows={4}
                                        placeholder="Describe your project or requirement in detail..."
                                        className="w-full text-sm p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none transition-all text-slate-800 placeholder:text-slate-400"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <DialogFooter className="mt-6 flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 w-full">
                        <button
                            type="button"
                            disabled={loading}
                            onClick={handleCancel}
                            className="w-full sm:w-1/2 py-2.5 px-4 text-sm font-semibold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/80 rounded-xl transition-all text-center cursor-pointer disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full sm:w-1/2 py-2.5 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-md hover:shadow-lg transition-all text-center inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Sending...</span>
                                </>
                            ) : (
                                <span>Send Request</span>
                            )}
                        </button>
                    </DialogFooter>
                </form>
            </Form>
        </>
    );
}

export default ConnectForm;
